import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { formatUSDT, formatNumber } from '../utils/helpers';
import NFTCard from '../components/NFTCard';
import Modal from '../components/Modal';
import Loader from '../components/Loader';
import './MyNFTs.css';

const NFTS_PER_PAGE = 50; // Pagination limit

const MyNFTs = () => {
  const { account, engineContract } = useWeb3();
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNFT, setSelectedNFT] = useState(null);
  const [totalValue, setTotalValue] = useState(0);
  const [totalNFTCount, setTotalNFTCount] = useState(0);

  useEffect(() => {
    fetchUserNFTs();
    const interval = setInterval(fetchUserNFTs, 30000);
    return () => clearInterval(interval);
  }, [account, engineContract]);

  const fetchUserNFTs = async () => {
    if (!engineContract || !account) {
      setLoading(false);
      return;
    }

    try {
      // Step 1: get the full ID list (single-pass fetch with large limit)
      const firstBatch = await engineContract.getUserNFTs(account, 0, 10000);
      const totalCount = firstBatch.total.toNumber();
      setTotalNFTCount(totalCount);

      let allNftIds = Array.from(firstBatch.nftIds);
      let offset = allNftIds.length;

      // If user has > 10000 NFTs, keep paging (rare)
      while (offset < totalCount) {
        const batch = await engineContract.getUserNFTs(account, offset, 10000);
        allNftIds = allNftIds.concat(Array.from(batch.nftIds));
        offset += batch.nftIds.length;
        if (batch.nftIds.length === 0) break;
      }

      if (allNftIds.length === 0) {
        setNfts([]);
        setTotalValue(0);
        return;
      }

      // Step 2: fetch ALL NFT details in parallel (biggest speedup — N×RTT → 1×RTT)
      const detailResults = await Promise.allSettled(
        allNftIds.map(id => engineContract.getNFTDetails(id))
      );

      const nftList = [];
      let total = 0;
      allNftIds.forEach((id, i) => {
        const r = detailResults[i];
        if (r.status !== 'fulfilled') return;
        const details = r.value;
        if (!details.isActive) return; // filter stale/inactive

        total += parseFloat(formatUSDT(details.purchasePrice));
        nftList.push({
          nftId: id.toString(),
          currentPrice: details.currentPrice,
          purchasePrice: details.purchasePrice,
          owner: details.nftOwner,
          lastPurchaseTime: details.lastPurchaseTime,
          isActive: details.isActive,
          isAutoMode: details.isAutoMode
        });
      });

      setNfts(nftList);
      setTotalValue(total);
    } catch (error) {
      console.error('Error fetching NFTs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader text="Loading Your NFTs" />;
  }

  return (
    <div className="mynfts-page">
      {/* Header */}
      <div className="mynfts-header animate-fade-up">
        <h1 className="page-title">
          <span className="title-icon pulse-icon">💎</span>
          My NFTs
        </h1>
      </div>

      {/* Stats - Responsive layout */}
      <div className="collection-stats glass-card animate-fade-up delay-1">
        <div className="stat">
          <span className="stat-value font-numbers">{nfts.length}</span>
          <span className="stat-label">Total NFTs</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat">
          <span className="stat-value font-numbers">${formatNumber(totalValue)}</span>
          <span className="stat-label">Total Value</span>
        </div>
        {/* Avg price only on desktop */}
        <div className="stat-divider desktop-only"></div>
        <div className="stat desktop-only">
          <span className="stat-value font-numbers">
            ${nfts.length > 0 ? formatNumber(totalValue / nfts.length) : '0'}
          </span>
          <span className="stat-label">Avg Price</span>
        </div>
      </div>

      {/* NFT Grid or Empty State */}
      {nfts.length === 0 ? (
        <div className="empty-collection animate-fade-up delay-2">
          <div className="empty-visual">
            <div className="empty-orb">
              <span className="empty-icon">📦</span>
            </div>
            <div className="empty-particles">
              {[...Array(8)].map((_, i) => (
                <span key={i} className="empty-particle" style={{ '--i': i }}></span>
              ))}
            </div>
          </div>
          <h3>No NFTs Yet</h3>
          <p>Start your collection by purchasing NFTs from the marketplace</p>
          <Link to="/market" className="btn btn-primary">
            <span>🛒</span> Browse Market
          </Link>
        </div>
      ) : (
        <div className="nft-grid">
          {nfts.map((nft, index) => (
            <div key={nft.nftId} className="animate-fade-up" style={{ animationDelay: `${(index + 2) * 0.05}s` }}>
              <NFTCard 
                nft={nft} 
                isOwned={true}
                onSelect={() => setSelectedNFT(nft)}
              />
            </div>
          ))}
        </div>
      )}

      {/* NFT Detail Modal */}
      <Modal
        isOpen={!!selectedNFT}
        onClose={() => setSelectedNFT(null)}
        title={`ADNFT#${selectedNFT?.nftId}`}
        size="small"
      >
        {selectedNFT && (
          <div className="nft-detail-modal">
            <div className="detail-visual">
              <div className="detail-hex">
                <span className="font-numbers">#{selectedNFT.nftId}</span>
              </div>
            </div>

            <div className="detail-info">
              <div className="info-row highlight">
                <span className="info-label">Your Purchase Price</span>
                <span className="info-value font-numbers price">${formatUSDT(selectedNFT.purchasePrice)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Current Market Price</span>
                <span className="info-value font-numbers">${formatUSDT(selectedNFT.currentPrice)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Status</span>
                <span className="info-value status-active">● Active</span>
              </div>
            </div>

            <div className="detail-note">
              <p>💡 When this NFT is purchased by another user, you will receive your principal (${formatUSDT(selectedNFT.purchasePrice)}) back plus your profit share.</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyNFTs;
